import {
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';

export type PairComparisonByFieldNameInput<TRow extends { fieldName?: string }> = {
	current: TRow[];
	previous: TRow[];
	hasComparison: boolean;
	matchField?: string;
	buildPreviousRow?: (previousRow: TRow | null, currentRow: TRow) => Record<string, any>;
};

const parseSubTotal = (row: any): number => {
	const value = parseFloat(row && row.subTotal);
	return isNaN(value) ? 0 : value;
};

export type PairComparisonByFieldNameResult = {
	current: any[];
	previous: any[];
	/** Y-axis category order — previous-only categories first (visual bottom), then `primaryChartRows` order. */
	categoryOrder: string[];
};

/**
 * Pairs current- and previous-period breakdown rows by category label (default `fieldName`).
 *
 * Emits a row in each series for every category that appears in EITHER period. Categories
 * that only have current data get a synthetic zero previous row (the default behaviour);
 * categories that only have previous data get a synthetic zero current row so the chart
 * still draws a band for them. Without the second case, vendors / problem types with prior
 * spend but no current spend would silently disappear from the chart while still showing up
 * in the CSV — which is what the user reported on the spend-by-vendor view.
 */
export const pairComparisonByFieldName = <TRow extends { fieldName?: string }>({
	current,
	previous,
	hasComparison,
	matchField = 'fieldName',
	buildPreviousRow,
}: PairComparisonByFieldNameInput<TRow>): PairComparisonByFieldNameResult => {
	const effectiveBuildPreviousRow =
		buildPreviousRow ||
		((previousRow: TRow | null, currentRow: TRow) => ({
			...(previousRow || {}),
			[matchField]: currentRow && currentRow[matchField],
			fieldName: (currentRow && currentRow.fieldName) || (currentRow && currentRow[matchField]),
			subTotal: previousRow ? (previousRow as any).subTotal : 0,
		}));

	if (!hasComparison || !previous || previous.length === 0) {
		const currentTagged = (current || []).map((row) => ({
			...row,
			[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT,
		}));
		const categoryOrder: string[] = [];
		const seenCategories = new Set<string>();
		(current || []).forEach((row) => {
			if (!row || row[matchField] === null || row[matchField] === undefined) return;
			const key = String(row[matchField]);
			if (!key || seenCategories.has(key)) return;
			seenCategories.add(key);
			categoryOrder.push(key);
		});
		return { current: currentTagged, previous: [], categoryOrder };
	}

	const currentByField = new Map<string, TRow>();
	(current || []).forEach((row) => {
		if (row && row[matchField]) {
			currentByField.set(String(row[matchField]), row);
		}
	});

	const previousByField = new Map<string, TRow>();
	(previous || []).forEach((row) => {
		if (row && row[matchField]) {
			previousByField.set(String(row[matchField]), row);
		}
	});

	// Keep current-period categories in the same order as `primaryChartRows` (single-mode chart
	// order). Prepend previous-only categories (visual bottom on the transposed chart), sorted
	// by comparison values in the same ascending/descending direction.
	const currentFieldNames: string[] = [];
	const seenCurrentFieldNames = new Set<string>();
	(current || []).forEach((row) => {
		if (!row || row[matchField] === null || row[matchField] === undefined) return;
		const key = String(row[matchField]);
		if (!key || seenCurrentFieldNames.has(key)) return;
		seenCurrentFieldNames.add(key);
		currentFieldNames.push(key);
	});

	const currentRows = current || [];
	// Match `primaryChartRows` sort direction: compare first vs last when 2+ rows; with a
	// single current row default to descending (vendor/leaderboard pages after filtering).
	const isAscendingSort =
		currentRows.length >= 2
			? parseSubTotal(currentRows[0]) <= parseSubTotal(currentRows[currentRows.length - 1])
			: false;

	const previousOnlyByField = new Map<string, TRow>();
	(previous || []).forEach((row) => {
		if (!row || row[matchField] === null || row[matchField] === undefined) return;
		const key = String(row[matchField]);
		if (!key || seenCurrentFieldNames.has(key)) return;
		// Last occurrence wins — matches `previousByField` below.
		previousOnlyByField.set(key, row);
	});
	const previousOnlyRows = Array.from(previousOnlyByField.values());
	previousOnlyRows.sort((rowA, rowB) => {
		const diff = parseSubTotal(rowA) - parseSubTotal(rowB);
		return isAscendingSort ? diff : -diff;
	});

	const orderedFieldNames = [
		...previousOnlyRows.map((row) => String(row[matchField])),
		...currentFieldNames,
	];

	const allCurrentRows: any[] = [];
	const allPreviousRows: any[] = [];

	orderedFieldNames.forEach((fieldName) => {
		const realCurrent = currentByField.get(fieldName);
		const realPrevious = previousByField.get(fieldName);

		if (realCurrent) {
			allCurrentRows.push({
				...realCurrent,
				[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT,
			});
		} else if (realPrevious) {
			// Synthetic zero current row so the chart still draws a band for previous-only
			// categories. Inherits structural fields from the previous row but zeros out the
			// value/percentage so no current bar paints.
			allCurrentRows.push({
				...(realPrevious as any),
				[matchField]: fieldName,
				subTotal: 0,
				percentageOfTotal: 0,
				[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT,
			});
		}

		// `buildPreviousRow` expects a current row to read structural fields off (e.g. fieldName
		// for label placement). Use the real current when available, otherwise a stub keyed by
		// the field name we're stitching.
		const baseCurrentForBuild: TRow =
			realCurrent ||
			({
				[matchField]: fieldName,
			} as unknown as TRow);
		allPreviousRows.push({
			...effectiveBuildPreviousRow(realPrevious || null, baseCurrentForBuild),
			[COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_PREVIOUS,
		});
	});

	return { current: allCurrentRows, previous: allPreviousRows, categoryOrder: orderedFieldNames };
};

/**
 * Interleaves previous + current rows per category so G2 dodge groups within each band.
 * Batch order `[...allPrevious, ...allCurrent]` causes previous-only categories to paint on
 * the negative side of the value axis when dodge is not scoped per category.
 */
export const buildInterleavedComparisonChartData = (
	paired: Pick<PairComparisonByFieldNameResult, 'current' | 'previous' | 'categoryOrder'>,
	categoryField: string
): any[] => {
	const previousByCategory = new Map<string, any>();
	(paired.previous || []).forEach((row) => {
		if (!row || row[categoryField] === null || row[categoryField] === undefined) return;
		previousByCategory.set(String(row[categoryField]), row);
	});
	const currentByCategory = new Map<string, any>();
	(paired.current || []).forEach((row) => {
		if (!row || row[categoryField] === null || row[categoryField] === undefined) return;
		currentByCategory.set(String(row[categoryField]), row);
	});

	return (paired.categoryOrder || []).reduce((rows: any[], categoryName) => {
		const prevRow = previousByCategory.get(categoryName);
		const currRow = currentByCategory.get(categoryName);
		if (prevRow) rows.push(prevRow);
		if (currRow) rows.push(currRow);
		return rows;
	}, []);
};
